import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (code, state) => {
    try {
      const response = await api.post('/auth/callback', { code, state });
      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  const logout = async () => {
    try {
      const response = await api.get('/auth/logout');
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);

      // Redirect to OIDC logout
      if (response.data.logoutUrl) {
        window.location.href = response.data.logoutUrl;
      }
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  const getLoginUrl = async () => {
    try {
      const response = await api.get('/auth/login');
      return response.data;
    } catch (error) {
      console.error('Get login URL error:', error);
      return null;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    getLoginUrl,
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
